/**
 * Tax Setup gate (Phase A). While tax tracking is on and the country tax setup is incomplete:
 *  - on intake/document routes: non-dismissible modal (complete setup, or owner turns tracking off with password)
 *  - elsewhere: slim banner with CTA. Also handles the backend 409 `tax_setup_required` via a window event.
 */
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Scale, Loader2, ShieldAlert, Landmark, CheckCircle2, Circle } from 'lucide-react';
import { API, useAuth } from '../../App';
import { setTaxGateOpen } from '../../utils/taxGateState';

const SKIP_PREFIXES = ['/onboarding', '/auth', '/signup', '/login', '/portal', '/help', '/privacy', '/terms', '/pricing', '/contact', '/close-account', '/admin', '/tax-setup', '/register', '/partner', '/partners', '/r/'];
const GATED_PREFIXES = ['/bank-feeds', '/banks', '/banking', '/invoices', '/create-invoice', '/bills', '/create-bill', '/credit-notes', '/manual-journals', '/documents/upload', '/open-banking', '/other-receipts', '/other-payments', '/other-transactions'];

export default function TaxSetupGate() {
  const { user, business, businessResolved } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [forced, setForced] = useState(false);
  const [offMode, setOffMode] = useState(false);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [bankCount, setBankCount] = useState(null);

  const isBusiness = (user?.account_type || 'business') !== 'personal';
  const ready = !!user && isBusiness && businessResolved && !!business?.id;

  const refresh = () => axios.get(`${API}/tax-setup/status`).then((r) => setStatus(r.data)).catch(() => setStatus(null));
  useEffect(() => { if (ready) refresh(); }, [ready, business?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const done = () => setStatus((s) => ({ ...(s || {}), required: false, complete: true }));
    const blocked = () => { setForced(true); refresh(); };
    window.addEventListener('akf-tax-setup-complete', done);
    window.addEventListener('akf-tax-setup-required', blocked);
    return () => { window.removeEventListener('akf-tax-setup-complete', done); window.removeEventListener('akf-tax-setup-required', blocked); };
  }, []);

  const skip = location.pathname === '/' || SKIP_PREFIXES.some((p) => location.pathname.startsWith(p));
  const gatedRoute = GATED_PREFIXES.some((p) => location.pathname.startsWith(p));
  // Legal form comes first for the banner (its own gate lives on Home); intake/document routes are always blocked
  // by the tax setup gate so it can never be masked by a later check (e.g. "Bank account required").
  const showDialog = !!(ready && !skip && status?.required && (gatedRoute || forced));
  useEffect(() => { setTaxGateOpen(showDialog); return () => setTaxGateOpen(false); }, [showDialog]);
  useEffect(() => {
    if (!showDialog) return;
    axios.get(`${API}/bank-accounts`).then((r) => setBankCount((r.data || []).length)).catch(() => setBankCount(null));
  }, [showDialog, business?.id]);
  if (!ready || skip || !status?.required || (!gatedRoute && !forced && business?.legal_form_confirmed !== true)) return null;
  const isOwner = business?.user_id === user?.id;
  const goSetup = () => { setForced(false); navigate(`/tax-setup?return=${encodeURIComponent(location.pathname)}`); };

  const turnOff = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/tax-setup/tracking`, { enabled: false, password });
      toast.success('Tax tracking turned off — no tax will be suggested or posted until you turn it back on');
      setStatus((s) => ({ ...s, required: false, tracking_on: false })); setForced(false);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not turn off tax tracking'); }
    finally { setBusy(false); }
  };

  if (gatedRoute || forced) {
    return (
      <Dialog open onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-lg z-[200] [&>button]:hidden" data-testid="tax-setup-gate" onEscapeKeyDown={(e) => e.preventDefault()} onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-[#283F8B]" /> Before you start</DialogTitle>
            <DialogDescription>
              Two quick things so every transaction and document is recorded correctly.
            </DialogDescription>
          </DialogHeader>
          {/* One checklist instead of stacked dialogs: tax setup (blocking) + bank account (next) */}
          <ul className="space-y-2" data-testid="setup-prereq-list">
            <li className="flex items-start gap-3 rounded-lg border border-[#283F8B]/20 bg-[#283F8B]/5 p-3" data-testid="setup-prereq-tax">
              <Scale className="w-4 h-4 mt-0.5 text-[#283F8B] shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900">Tax setup <span className="text-xs font-normal text-slate-500">· {status.percent}% done{status.missing?.length ? ` · ${status.missing.length} question${status.missing.length === 1 ? '' : 's'} left` : ''}</span></p>
                <p className="text-xs text-slate-600">Tax tracking is on, so documents and bank transactions need this to post correctly.</p>
              </div>
            </li>
            <li className={`flex items-start gap-3 rounded-lg border p-3 ${bankCount === 0 ? 'border-slate-200 bg-white' : 'border-emerald-200 bg-emerald-50'}`} data-testid="setup-prereq-bank">
              {bankCount === 0 ? <Circle className="w-4 h-4 mt-0.5 text-slate-400 shrink-0" /> : <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900">Bank account {bankCount === 0 ? <span className="text-xs font-normal text-slate-500">· next step</span> : bankCount == null ? null : <span className="text-xs font-normal text-emerald-700">· added</span>}</p>
                {bankCount === 0 && <p className="text-xs text-slate-600">Invoices and bills need an account to land in. We&apos;ll ask for it right after tax setup.</p>}
              </div>
              <Landmark className="w-4 h-4 mt-0.5 text-slate-400 shrink-0" />
            </li>
          </ul>
          {!offMode ? (
            <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
              {isOwner && <Button variant="ghost" onClick={() => setOffMode(true)} className="text-slate-600" data-testid="tax-setup-gate-off-btn">Turn tax tracking off</Button>}
              <Button onClick={goSetup} className="bg-[#283F8B] hover:bg-[#1f326f]" data-testid="tax-setup-gate-cta">Complete tax setup</Button>
            </div>
          ) : (
            <div className="space-y-3" data-testid="tax-setup-gate-off-panel">
              <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800"><ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" /><span>Turning tax tracking off means no VAT, sales tax or withholding will be suggested, captured or posted anywhere in AkountFly. Your filings will not reconcile to your books. Owner password required.</span></div>
              <Input type="password" placeholder="Your password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="tax-setup-gate-password" />
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setOffMode(false)} data-testid="tax-setup-gate-off-cancel">Back</Button>
                <Button variant="destructive" onClick={turnOff} disabled={busy || !password} data-testid="tax-setup-gate-off-confirm">{busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Turn off tax tracking</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <div className="sticky top-0 z-40 bg-[#283F8B] text-white text-sm px-4 py-2 flex items-center gap-3" data-testid="tax-setup-banner">
      <Scale className="w-4 h-4 shrink-0" />
      <span className="flex-1 truncate">Tax setup {status.percent}% complete — finish it to connect banks, upload statements and create documents.</span>
      <button type="button" onClick={goSetup} className="px-3 py-1 rounded-full bg-white text-[#283F8B] text-xs font-medium hover:bg-slate-100" data-testid="tax-setup-banner-cta">Complete tax setup</button>
    </div>
  );
}
